import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageInput } from "./MessageInput";
import { ChatMessage } from "./ChatMessage";
import { Alert } from "../ui/alert";
import { type Message } from "./types";
import { useMessageSubscription } from "@hooks/useMessageSubscription";
import { PaginationErrorBanner } from "./PaginationErrorBanner";
import { MessageSkeleton } from "./MessageSkeleton";
import { SmartLoadingIndicator } from "./SmartLoadingIndicator";
import { useThrottledScroll } from "../../hooks/useThrottledScroll";
import { usePaginationTrigger } from "../../hooks/usePaginationTrigger";
import { type PaginationError } from "../../utils/paginationErrors";
import { type PaginationLoadingState } from "../../types/pagination";

interface ChatInterfaceProps {
  conversationId: string;
  assistantName?: string;
  messages?: Message[];
  onMessageSent?: (message: Message) => void;
  loading?: boolean;
  error?: string;
  height?: string;
  className?: string;
  showHeader?: boolean;
  // Pagination props  
  onLoadPrevious?: () => Promise<unknown>;
  onLoadMore?: () => Promise<unknown>;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
  loadingPrevious?: boolean;
  loadingMore?: boolean;
  // Error handling props
  paginationError?: PaginationError | null;
  isRetrying?: boolean;
  retryCount?: number;
  maxRetries?: number;
  onClearError?: () => void;
  // Enhanced loading state props
  loadingState?: PaginationLoadingState;
  loadingProgress?: number;
  estimatedMessageCount?: number;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  conversationId,
  assistantName = "Personal Assistant",
  messages = [],
  onMessageSent,
  loading = false,
  error,
  height = "h-[calc(100vh-200px)]", // Subtract space for page header and padding
  className = "",
  showHeader = true,
  // Pagination props
  onLoadPrevious,
  onLoadMore,
  hasPreviousPage = false,
  hasNextPage = false,
  loadingPrevious = false,
  loadingMore = false,
  // Error handling props
  paginationError,
  isRetrying = false,
  retryCount = 0,
  maxRetries = 3,
  onClearError,
  // Enhanced loading state props
  loadingState,
  loadingProgress = 0,
  estimatedMessageCount = 0,
}) => {
  const [optimisticMessages, setOptimisticMessages] = useState<Message[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  
  // Refs for loading states to prevent observer recreation
  const loadingPreviousRef = useRef(false);
  const loadingMoreRef = useRef(false);
  
  // Scroll position tracking for pagination
  const previousScrollHeightRef = useRef<number>(0);
  const scrollPositionRef = useRef<number>(0);
  const isNearBottomRef = useRef<boolean>(true);
  
  // Keep refs in sync with props
  useEffect(() => {
    loadingPreviousRef.current = loadingPrevious;
  }, [loadingPrevious]);
  
  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  // Check if user is near bottom of scroll container
  const checkIsNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return false;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 100; // Consider "near bottom" if within 100px
    return scrollHeight - scrollTop - clientHeight <= threshold;
  }, []);

  // Preserve scroll position before loading previous messages
  useEffect(() => {
    if (loadingPrevious && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      previousScrollHeightRef.current = container.scrollHeight;
      scrollPositionRef.current = container.scrollTop;
      isNearBottomRef.current = checkIsNearBottom();
    }
  }, [loadingPrevious, checkIsNearBottom]);

  // Restore scroll position after previous messages are loaded
  useEffect(() => {
    if (!loadingPrevious && messagesContainerRef.current && previousScrollHeightRef.current > 0) {
      const container = messagesContainerRef.current;
      const currentScrollHeight = container.scrollHeight;
      const heightDifference = currentScrollHeight - previousScrollHeightRef.current;
      
      if (heightDifference > 0) {
        // New messages were added to the top, adjust scroll position
        container.scrollTop = scrollPositionRef.current + heightDifference;
      }
      
      // Reset tracking refs
      previousScrollHeightRef.current = 0;
      scrollPositionRef.current = 0;
    }
  }, [loadingPrevious]);

  // Combine props messages with optimistic messages, filtering out duplicates
  const allMessages = useMemo(() => {
    // Create a Set of message IDs from props messages for fast lookup
    const existingMessageIds = new Set(messages.map(msg => msg.id));

    // Filter optimistic messages to exclude ones that already exist in props messages
    const uniqueOptimisticMessages = optimisticMessages.filter(
      msg => !existingMessageIds.has(msg.id),
    );

    return [...messages, ...uniqueOptimisticMessages];
  }, [messages, optimisticMessages]);

  // Handle new messages from subscription
  const handleNewMessage = useCallback((newMessage: Message) => {
    console.log("Received new message from subscription:", newMessage);

    // Remove from optimistic messages if it was there (now confirmed by server)
    setOptimisticMessages(prev => {
      const filtered = prev.filter(msg => msg.id !== newMessage.id);
      if (filtered.length !== prev.length) {
        console.log(`Removed optimistic message ${newMessage.id} - now in server messages`);
      }
      return filtered;
    });

    // Note: The message should automatically appear in the UI through the Apollo cache update
    // in useMessageSubscription hook, which will trigger a re-render of useConversationMessages
  }, []);

  // Subscribe to real-time messages
  const { subscriptionError } = useMessageSubscription({
    conversationId,
    onNewMessage: handleNewMessage,
    enabled: !!conversationId,
  });

  // Handle scroll-based pagination with throttling
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    
    // Add null safety check for container
    if (!container) {
      console.warn("Scroll container is null, skipping scroll handler");
      return;
    }
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Update isNearBottom tracking for auto-scroll logic
    isNearBottomRef.current = checkIsNearBottom();
    
    // Load previous messages when scrolled to the top
    if (scrollTop <= 10 && hasPreviousPage && !loadingPrevious && onLoadPrevious) {
      console.log("Loading previous messages...");
      onLoadPrevious();
    }
    
    // Load more messages when scrolled near the bottom (optional, for forward pagination)
    if (scrollHeight - scrollTop - clientHeight <= 10 && hasNextPage && !loadingMore && onLoadMore) {
      console.log("Loading more messages...");
      onLoadMore();
    }
  }, [hasPreviousPage, hasNextPage, loadingPrevious, loadingMore, onLoadPrevious, onLoadMore, checkIsNearBottom]);

  // Throttle scroll events for better performance
  const throttledHandleScroll = useThrottledScroll(handleScroll, 100);

  // Stable callbacks for pagination to prevent observer recreation
  const handleLoadPreviousStable = useCallback(() => {
    if (onLoadPrevious && hasPreviousPage && !loadingPreviousRef.current) {
      onLoadPrevious();
    }
  }, [onLoadPrevious, hasPreviousPage]); // No loadingPrevious dependency!

  const handleLoadMoreStable = useCallback(() => {
    if (onLoadMore && hasNextPage && !loadingMoreRef.current) {
      onLoadMore();
    }
  }, [onLoadMore, hasNextPage]); // No loadingMore dependency!

  // Alternative: Use Intersection Observer for even better performance
  const { topSentinelRef, bottomSentinelRef } = usePaginationTrigger(messagesContainerRef, {
    onTopReached: handleLoadPreviousStable,
    onBottomReached: handleLoadMoreStable,
    rootMargin: '50px',
    enabled: !!(onLoadPrevious || onLoadMore),
    cooldownMs: 2000, // 2 second cooldown to prevent rapid re-triggering
  });

  // Handle pagination error retry
  const handleRetryPagination = useCallback(() => {
    // Determine which operation to retry based on the last attempted action
    // For now, we'll clear the error and let the user manually retry
    onClearError?.();
  }, [onClearError]);

  // Debug logging for development (can be removed in production)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development' && loadingState?.isLoading) {
      console.log('Loading state:', { 
        direction: loadingState.direction, 
        progress: loadingProgress,
        estimatedCount: estimatedMessageCount 
      });
    }
  }, [loadingState, loadingProgress, estimatedMessageCount]);

  // Clean up optimistic messages that now exist in server messages
  useEffect(() => {
    if (messages.length > 0 && optimisticMessages.length > 0) {
      const serverMessageIds = new Set(messages.map(msg => msg.id));
      const remainingOptimistic = optimisticMessages.filter(msg => !serverMessageIds.has(msg.id));

      if (remainingOptimistic.length !== optimisticMessages.length) {
        console.log("Cleaning up optimistic messages that are now in server messages");
        setOptimisticMessages(remainingOptimistic);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]); // Only depend on messages, not optimisticMessages to avoid infinite loop

  // Smart auto-scroll: only scroll to bottom for new messages, not when loading previous
  useEffect(() => {
    // Don't auto-scroll if we're loading previous messages
    if (loadingPrevious) return;
    
    // Only auto-scroll if user was near bottom or if this is the first load
    if (isNearBottomRef.current || allMessages.length === 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [allMessages, loadingPrevious]);

  const handleMessageSent = (newMessage: Message) => {
    // Add to optimistic messages for immediate UI feedback
    setOptimisticMessages(prev => [...prev, newMessage]);
    onMessageSent?.(newMessage);
  };

  return (
    <div className={`flex flex-col ${height} ${className}`}>
      {showHeader && (
        <div className="border-b bg-background p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Chat with {assistantName}</h2>
            {loading && <span className="text-sm text-muted-foreground">Loading...</span>}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Send messages to communicate with your AI assistant
          </p>
        </div>
      )}

      {/* Pagination Error Banner */}
      {paginationError && (
        <PaginationErrorBanner
          error={paginationError}
          isRetrying={isRetrying}
          retryCount={retryCount}
          maxRetries={maxRetries}
          onRetry={handleRetryPagination}
          onDismiss={onClearError || (() => {})}
        />
      )}

      {/* Messages Area - Takes up remaining space */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4" 
        data-testid="message-list"
        onScroll={throttledHandleScroll}
      >
        {/* Top sentinel for intersection observer */}
        <div ref={topSentinelRef} className="h-1" />

        {/* Enhanced loading indicator for previous messages */}
        {loadingPrevious && (
          <>
            <SmartLoadingIndicator
              direction="previous"
              estimatedCount={estimatedMessageCount}
              isRetrying={isRetrying}
              retryCount={retryCount}
            />
            {/* Show skeleton loaders for better perceived performance */}
            <MessageSkeleton count={Math.min(estimatedMessageCount || 3, 5)} />
          </>
        )}

        {error && <Alert variant="destructive">{error}</Alert>}
        {subscriptionError && (
          <Alert variant="destructive">
            Real-time updates unavailable: {subscriptionError.message}
          </Alert>
        )}

        {allMessages.length === 0 && !loading && (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <p className="text-lg mb-2">Start a conversation</p>
              <p className="text-sm">Send a message to your {assistantName} to get started.</p>
            </div>
          </div>
        )}

        {allMessages.map(message => (
          <ChatMessage
            key={message.id}
            id={message.id}
            content={message.content}
            senderRole={message.senderRole}
            timestamp={message.timestamp}
            metadata={message.metadata}
          />
        ))}

        {/* Enhanced loading indicator for more messages */}
        {loadingMore && (
          <>
            <SmartLoadingIndicator
              direction="next"
              estimatedCount={estimatedMessageCount}
              isRetrying={isRetrying}
              retryCount={retryCount}
            />
            {/* Show skeleton loaders for better perceived performance */}
            <MessageSkeleton count={Math.min(estimatedMessageCount || 3, 5)} />
          </>
        )}

        {/* Bottom sentinel for intersection observer */}
        <div ref={bottomSentinelRef} className="h-1" />
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input Area - Stuck to bottom */}
      <div className="border-t bg-background p-4">
        <MessageInput
          conversationId={conversationId}
          onMessageSent={handleMessageSent}
          disabled={loading}
          placeholder={`Send a message to ${assistantName}...`}
        />
      </div>
    </div>
  );
};
